import React, { useState, useContext, useEffect } from 'react'
import DatabaseContext from '../../../../contexts/Database/DatabaseContext'
import AuthenticationContext from '../../../../contexts/Authentication/AuthenticationContext'
import ToasterContext from '../../../../contexts/Toaster/ToasterContext'
import { getCacheStats, clearAllCache } from '../../../../contexts/Database/Database.services/db'
import advancedFuzzySearch from './DebugConsole.functions/advancedFuzzySearch'
import './DebugConsole.css'

// Using backend endpoints only (production mode)

const DebugConsole = () => {
  // 💾 Simple state restoration from localStorage
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem('debugConsoleState')
      if (saved) {
        const state = JSON.parse(saved)
        // Only restore if saved within last 5 minutes
        if (Date.now() - state.timestamp < 5 * 60 * 1000) {
          return state
        }
      }
    } catch (error) {
      console.warn('Failed to restore debug console state:', error)
    }
    return {}
  }

  const savedState = getInitialState()

  const [isExpanded, setIsExpanded] = useState(savedState.isExpanded || false)
  const [isHidden, setIsHidden] = useState(savedState.isHidden || false)
  const [activeTab, setActiveTab] = useState(savedState.activeTab || 'user')
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const [tempAttributes, setTempAttributes] = useState(null)
  const [isEditingAttributes, setIsEditingAttributes] = useState(savedState.isEditingAttributes || false)
  const [attributesSaved, setAttributesSaved] = useState(false) // Track if step 1 is done (permissions)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [cacheStats, setCacheStats] = useState({ totalSize: 0, totalRecords: 0, userStats: {} })
  const [isClearingCache, setIsClearingCache] = useState(false)
  const [isSavingAttributes, setIsSavingAttributes] = useState(false)
  const [backendUsers, setBackendUsers] = useState([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)

  // 💾 Save state to localStorage whenever important state changes
  useEffect(() => {
    const stateToSave = {
      isExpanded,
      isHidden,
      activeTab,
      isEditingAttributes,
      timestamp: Date.now(),
    }
    localStorage.setItem('debugConsoleState', JSON.stringify(stateToSave))
  }, [isExpanded, isHidden, activeTab, isEditingAttributes])

  // 📊 Load cache stats when cache tab is active
  useEffect(() => {
    if (activeTab === 'cache') {
      loadCacheStats()
    }
  }, [activeTab])

  const loadCacheStats = async () => {
    try {
      const stats = await getCacheStats()
      setCacheStats(stats)
    } catch (error) {
      console.error('Failed to load cache stats:', error)
    }
  }

  const handleClearCache = async () => {
    setIsClearingCache(true)
    try {
      await clearAllCache()
      console.log('✅ Cache cleared successfully')
      await loadCacheStats() // Reload stats after clearing
    } catch (error) {
      console.error('❌ Error clearing cache:', error)
    } finally {
      setIsClearingCache(false)
    }
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`
  }

  const {
    user,
    impersonateUser,
    getAvailableUsers,
    isDevelopment,
    userCheckSession,
    startImpersonation,
    stopImpersonation,
    switchImpersonation,
    getImpersonationStatus,
    getAvailableUsersFromBackend,
  } = useContext(AuthenticationContext)

  const databaseContext = useContext(DatabaseContext)
  const {
    bankTransfers = [],
    dashboardSalesOrders = [],
    transfersOrdersMatches = [],
    isLoading = false,
    refetch,
    setDocument,
    getDocument,
  } = databaseContext || {}

  // Context pour les notifications
  const { showToast } = useContext(ToasterContext) || {}

  // Using backend mode only (production)
  const isBackendMode = true
  const availableUsers = isBackendMode
    ? backendUsers
    : (isDevelopment ? getAvailableUsers() : [])
  const filteredUsers = advancedFuzzySearch(availableUsers, userSearch)

  // Check if currently impersonating (backend)
  const impersonationStatus = isBackendMode ? getImpersonationStatus() : null
  const isCurrentlyImpersonating = !!impersonationStatus

  // Load users when component mounts or when switching to backend mode
  useEffect(() => {
    if (isBackendMode && backendUsers.length === 0) {
      loadBackendUsers()
    }
  }, [isBackendMode])

  const loadBackendUsers = async () => {
    if (isLoadingUsers) return

    setIsLoadingUsers(true)
    try {
      const users = await getAvailableUsersFromBackend()
      setBackendUsers(users)
      console.log(`📊 Loaded ${users.length} users from backend`)
    } catch (error) {
      console.error('❌ Failed to load backend users:', error)
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const handleUserChange = async (userEmailOrId) => {
    if (isBackendMode) {
      // Backend impersonation
      const targetUser = availableUsers.find(u => u.email === userEmailOrId || u.id === userEmailOrId)
      if (!targetUser) {
        console.error('User not found:', userEmailOrId)
        return
      }

      // Use switchImpersonation if already impersonating, otherwise use startImpersonation
      const impersonationFunction = isCurrentlyImpersonating ? switchImpersonation : startImpersonation
      const actionText = isCurrentlyImpersonating ? 'switching to' : 'starting impersonation for'
      
      console.log(`🔄 [BACKEND] ${actionText}: ${targetUser.email}`)
      
      const result = await impersonationFunction(targetUser.id, 'Debug Console impersonation')
      if (result.success) {
        setUserDropdownOpen(false)
        setUserSearch('')
        console.log('🔄 [BACKEND] User switched to:', targetUser.email)
        
        // If the result indicates a page reload will happen, don't do anything else
        if (result.willReload) {
          console.log('🔄 Page will reload for impersonation switch')
        }
      } else {
        console.error('Failed to change user:', result.error)
      }
    } else {
      // Legacy impersonation
      const result = impersonateUser(userEmailOrId)
      if (result.success) {
        setUserDropdownOpen(false)
        setUserSearch('')
        console.log('🔄 [LEGACY] User switched to:', userEmailOrId)
      } else {
        console.error('Failed to switch user:', result.error)
      }
    }
  }

  const handleStopImpersonation = async () => {
    if (!isBackendMode || !isCurrentlyImpersonating) return

    const result = await stopImpersonation()
    if (result.success) {
      console.log('✅ [BACKEND] Stopped impersonation, returning to admin user')
    } else {
      console.error('Failed to stop backend impersonation:', result.error)
    }
  }

  const handlePreviousUser = () => {
    if (availableUsers.length === 0) return
    const currentIndex = availableUsers.findIndex(u => u.email === user?.email)
    const previousIndex = currentIndex <= 0 ? availableUsers.length - 1 : currentIndex - 1
    const previousUser = availableUsers[previousIndex]
    console.log('⬅️ Switching to previous user:', previousUser.email)
    handleUserChange(previousUser.email)
  }

  const handleNextUser = () => {
    if (availableUsers.length === 0) return
    const currentIndex = availableUsers.findIndex(u => u.email === user?.email)
    const nextIndex = currentIndex >= availableUsers.length - 1 ? 0 : currentIndex + 1
    const nextUser = availableUsers[nextIndex]
    console.log('➡️ Switching to next user:', nextUser.email)
    handleUserChange(nextUser.email)
  }

  const handleRefreshData = async () => {
    if (!refetch) {
      console.warn('Refetch function not available')
      return
    }

    setIsRefreshing(true)
    try {
      await refetch()
      console.log('✅ Data refreshed successfully from Debug Console')

      // If we just refreshed after saving attributes, close the editor
      if (attributesSaved) {
        setIsEditingAttributes(false)
        setAttributesSaved(false) // Reset the state
      }
    } catch (error) {
      console.error('❌ Error refreshing data:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  // Fonction intelligente pour formater les valeurs utilisateur
  const formatUserValue = (value) => {
    if (value === null || value === undefined) return 'N/A'
    if (Array.isArray(value)) {
      if (value.length === 0) return 'Empty array'
      return value.join(', ')
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2)
      } catch {
        return '[Object]'
      }
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    return String(value)
  }

  // État pour stocker les attributs complets de l'utilisateur
  const [completeUserAttributes, setCompleteUserAttributes] = useState(null)

  // Charger automatiquement les attributs complets quand l'utilisateur change
  useEffect(() => {
    if (user?.id && getDocument) {
      loadCompleteUserAttributes()
    }
  }, [user?.id, getDocument])

  // Affichage dynamique de toutes les propriétés utilisateur
  const getUserDisplayData = () => {
    if (!user) {
      return {
        status: 'Not logged in',
        data: {},
      }
    }

    // Utiliser les attributs complets si disponibles, sinon l'utilisateur de base
    const userToDisplay = completeUserAttributes || user

    // DEBUG: Log complet de l'objet user pour voir ce que le backend renvoie
    console.log('🔍 [DebugConsole] User object from backend:', user)
    console.log('🔍 [DebugConsole] Complete user attributes:', completeUserAttributes)
    console.log('🔍 [DebugConsole] User to display:', userToDisplay)
    console.log('🔍 [DebugConsole] User keys:', Object.keys(userToDisplay))
    console.log('🔍 [DebugConsole] User email:', userToDisplay.email)
    console.log('🔍 [DebugConsole] User role:', userToDisplay.role)
    console.log('🔍 [DebugConsole] Is SuperAdmin by role?', userToDisplay.role === 'SuperAdmin')

    // Exclure les champs internes/techniques
    const excludeFields = ['iat', 'exp', 'impersonation']
    const userData = {}

    Object.entries(userToDisplay).forEach(([key, value]) => {
      if (!excludeFields.includes(key)) {
        userData[key] = formatUserValue(value)
      }
    })

    console.log('🔍 [DebugConsole] Filtered user data for display:', userData)

    return {
      status: 'Logged in',
      data: userData,
    }
  }

  // Fonction pour récupérer les attributs complets de l'utilisateur
  const loadCompleteUserAttributes = async () => {
    if (!user?.id || !getDocument) return

    console.log('🔍 [DebugConsole] Loading complete user attributes via getDocument...')
    
    try {
      const result = await getDocument('users', user.id)
      
      if (result.success) {
        console.log('✅ [DebugConsole] Complete user attributes loaded:', result.data)
        setCompleteUserAttributes(result.data)
        return result.data
      } else {
        console.error('❌ [DebugConsole] Failed to load user attributes:', result.error)
        return null
      }
    } catch (error) {
      console.error('❌ [DebugConsole] Error loading user attributes:', error)
      return null
    }
  }

  const userInfo = getUserDisplayData()

  // SUPPRIMÉ : Plus de vérification SuperAdmin côté frontend - sécurité côté backend

  const handleSaveAttributes = async () => {
    if (!user?.id) return

    setIsSavingAttributes(true)
    try {
      // Utiliser setDocument pour modifier directement les attributs SuperAdmin en base
      const currentUser = completeUserAttributes || user
      const data = {
        ...currentUser,
        ...tempAttributes,
      }

      console.log('🔧 [DebugConsole] Updating SuperAdmin attributes via setDocument...', tempAttributes)

      const result = await setDocument('users', user.id, data)

      if (result.success) {
        console.log('✅ [DebugConsole] User attributes updated successfully in database')

        // Recharger les données utilisateur depuis le backend
        await userCheckSession()

        setAttributesSaved(true)

        showToast({
          message: '✅ Attributs utilisateur mis à jour avec succès !',
          type: 'success',
          duration: 5000,
        })
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      console.error('❌ [DebugConsole] Error updating user attributes:', error)
      showToast({
        message: `❌ Erreur lors de la mise à jour: ${error.message}`,
        type: 'error',
        duration: 8000,
      })
    } finally {
      setIsSavingAttributes(false)
    }
  }

  // Options prédéfinies pour les différents types d'attributs
  const PREDEFINED_OPTIONS = {
    roles: ['SuperAdmin', 'Admin', 'Sales', 'Finance', 'Production', 'Logistics', 'Management'],
    entities: ['ALL', 'WGE', 'WGUK', 'WGA', 'WGF', 'WGI', 'WGS', 'WGNL'],
    currencies: ['ALL', 'EUR', 'USD', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'SEK', 'NOK', 'DKK'],
    zones: ['ALL', '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15'],
    departments: ['IT', 'Sales', 'Finance', 'Production', 'Logistics', 'Management', 'HR', 'Marketing'],
  }

  // Éditeur spécialisé pour les attributs de permissions
  const renderSpecializedAttributeEditor = (key, value) => {
    const handleValueChange = (newValue) => {
      setTempAttributes(prev => ({
        ...prev,
        [key]: newValue,
      }))
    }

    // Éditeur pour les rôles (array avec checkboxes)
    if (key === 'roles' && Array.isArray(value)) {
      return (
        <div className="debug-console-permissions-editor">
          <div className="debug-console-checkbox-grid">
            {PREDEFINED_OPTIONS.roles.map(role => (
              <label key={role} className="debug-console-checkbox-label">
                <input
                  type="checkbox"
                  checked={value.includes(role)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleValueChange([...value, role])
                    } else {
                      handleValueChange(value.filter(r => r !== role))
                    }
                  }}
                />
                {role}
              </label>
            ))}
          </div>
          <div className="debug-console-manual-input">
            <input
              type="text"
              placeholder="Add custom role..."
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  const customRole = e.target.value.trim()
                  if (!value.includes(customRole)) {
                    handleValueChange([...value, customRole])
                  }
                  e.target.value = ''
                }
              }}
              className="debug-console-custom-input"
            />
            <small className="debug-console-hint">Press Enter to add custom role</small>
          </div>
        </div>
      )
    }

    // Éditeur pour les entités (array avec checkboxes)
    if (key === 'entities' && Array.isArray(value)) {
      return (
        <div className="debug-console-permissions-editor">
          <div className="debug-console-checkbox-grid">
            {PREDEFINED_OPTIONS.entities.map(entity => (
              <label key={entity} className={`debug-console-checkbox-label ${entity === 'ALL' ? 'special-value' : ''}`}>
                <input
                  type="checkbox"
                  checked={value.includes(entity)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Si on coche "ALL", décocher les autres
                      if (entity === 'ALL') {
                        handleValueChange(['ALL'])
                      } else {
                        // Si on coche autre chose, retirer "ALL"
                        const newValue = [...value.filter(v => v !== 'ALL'), entity]
                        handleValueChange(newValue)
                      }
                    } else {
                      handleValueChange(value.filter(ent => ent !== entity))
                    }
                  }}
                />
                {entity === 'ALL' ? '🌍 ALL ENTITIES' : entity}
              </label>
            ))}
          </div>
        </div>
      )
    }

    // Éditeur pour les devises (array avec checkboxes)
    if (key === 'currencies' && Array.isArray(value)) {
      return (
        <div className="debug-console-permissions-editor">
          <div className="debug-console-checkbox-grid">
            {PREDEFINED_OPTIONS.currencies.map(currency => (
              <label key={currency} className={`debug-console-checkbox-label ${currency === 'ALL' ? 'special-value' : ''}`}>
                <input
                  type="checkbox"
                  checked={value.includes(currency)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Si on coche "ALL", décocher les autres
                      if (currency === 'ALL') {
                        handleValueChange(['ALL'])
                      } else {
                        // Si on coche autre chose, retirer "ALL"
                        const newValue = [...value.filter(v => v !== 'ALL'), currency]
                        handleValueChange(newValue)
                      }
                    } else {
                      handleValueChange(value.filter(c => c !== currency))
                    }
                  }}
                />
                {currency === 'ALL' ? '💰 ALL CURRENCIES' : currency}
              </label>
            ))}
          </div>
        </div>
      )
    }

    // Éditeur pour les zones (array avec checkboxes)
    if (key === 'zones' && Array.isArray(value)) {
      return (
        <div className="debug-console-permissions-editor">
          <div className="debug-console-checkbox-grid">
            {PREDEFINED_OPTIONS.zones.map(zone => (
              <label key={zone} className={`debug-console-checkbox-label ${zone === 'ALL' ? 'special-value' : ''}`}>
                <input
                  type="checkbox"
                  checked={value.includes(zone)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Si on coche "ALL", décocher les autres
                      if (zone === 'ALL') {
                        handleValueChange(['ALL'])
                      } else {
                        // Si on coche autre chose, retirer "ALL"
                        const newValue = [...value.filter(v => v !== 'ALL'), zone]
                        handleValueChange(newValue)
                      }
                    } else {
                      handleValueChange(value.filter(z => z !== zone))
                    }
                  }}
                />
                {zone === 'ALL' ? '🗺️ ALL ZONES' : `Zone ${zone}`}
              </label>
            ))}
          </div>
        </div>
      )
    }

    // Éditeur pour les départements (array avec checkboxes)
    if (key === 'departments' && Array.isArray(value)) {
      return (
        <div className="debug-console-permissions-editor">
          <div className="debug-console-checkbox-grid">
            {PREDEFINED_OPTIONS.departments.map(dept => (
              <label key={dept} className="debug-console-checkbox-label">
                <input
                  type="checkbox"
                  checked={value.includes(dept)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleValueChange([...value, dept])
                    } else {
                      handleValueChange(value.filter(d => d !== dept))
                    }
                  }}
                />
                {dept}
              </label>
            ))}
          </div>
        </div>
      )
    }

    // Éditeur pour les niveaux (objet avec checkboxes)
    if (key === 'levels' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return (
        <div className="debug-console-permissions-editor">
          <div className="debug-console-checkbox-grid">
            {['n1', 'n2', 'n3'].map(level => (
              <label key={level} className="debug-console-checkbox-label">
                <input
                  type="checkbox"
                  checked={value[level] === true}
                  onChange={(e) => {
                    handleValueChange({
                      ...value,
                      [level]: e.target.checked
                    })
                  }}
                />
                Level
                {' '}
                {level.toUpperCase()}
              </label>
            ))}
          </div>
        </div>
      )
    }

    // Éditeur pour le rôle simple (string avec select)
    if (key === 'role' && typeof value === 'string') {
      return (
        <select
          className="debug-console-attribute-input"
          value={value}
          onChange={(e) => handleValueChange(e.target.value)}
        >
          <option value="">Select role...</option>
          {PREDEFINED_OPTIONS.roles.map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
      )
    }

    // Fallback vers l'éditeur générique
    return renderGenericAttributeEditor(key, value)
  }

  // Éditeur générique pour les attributs SuperAdmin (version simplifiée)
  const renderGenericAttributeEditor = (key, value) => {
    const handleValueChange = (newValue) => {
      setTempAttributes(prev => ({
        ...prev,
        [key]: newValue,
      }))
    }

    // Si c'est un tableau
    if (Array.isArray(value)) {
      return (
        <textarea
          className="debug-console-attribute-input"
          value={value.join(', ')}
          onChange={(e) => {
            const arrayValue = e.target.value.split(',').map(item => item.trim()).filter(item => item)
            handleValueChange(arrayValue)
          }}
          placeholder="Separate values with commas"
          rows={2}
        />
      )
    }

    // Si c'est un objet
    if (typeof value === 'object' && value !== null) {
      return (
        <textarea
          className="debug-console-attribute-input"
          value={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try {
              const objectValue = JSON.parse(e.target.value)
              handleValueChange(objectValue)
            } catch {
              // Garde la valeur même si JSON invalide, on validera au save
            }
          }}
          placeholder="Valid JSON object"
          rows={4}
        />
      )
    }

    // Si c'est un boolean
    if (typeof value === 'boolean') {
      return (
        <select
          className="debug-console-attribute-input"
          value={value.toString()}
          onChange={(e) => handleValueChange(e.target.value === 'true')}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      )
    }

    // Par défaut, input text
    return (
      <input
        type="text"
        className="debug-console-attribute-input"
        value={value || ''}
        onChange={(e) => handleValueChange(e.target.value)}
        placeholder={`Enter ${key}`}
      />
    )
  }

  const renderSuperAdminControls = () => {
    console.log('🔍 [DebugConsole] renderSuperAdminControls called')
    console.log('🔍 [DebugConsole] user:', user)

    const currentAttributes = tempAttributes || completeUserAttributes || user
    console.log('🔍 [DebugConsole] currentAttributes for editing:', currentAttributes)

    return (
      <div className="debug-console-superadmin">
        <h4 className="debug-console-section-title">
          User Attributes Editor
          <button
            type="button"
            className="debug-console-edit-button"
            onClick={() => {
              if (!isEditingAttributes) {
                // Utiliser les attributs complets ou l'utilisateur de base, mais exclure l'ID
                const fullUser = completeUserAttributes || user
                const { id, ...editableAttributes } = fullUser
                console.log('🔧 [DebugConsole] Initializing edit mode with (without ID):', editableAttributes)
                setTempAttributes({ ...editableAttributes })
                setAttributesSaved(false)
              } else {
                setAttributesSaved(false)
              }
              setIsEditingAttributes(!isEditingAttributes)
            }}
          >
            {isEditingAttributes ? '❌ Cancel' : '✏️ Edit All Attributes'}
          </button>
        </h4>

        {isEditingAttributes ? (
          <div className="debug-console-attributes-editor">
            <div className="debug-console-editor-hint">
              💡 Edit user permissions and attributes. Use checkboxes for multiple selections.
            </div>

            {/* Permissions Section */}
            <div className="debug-console-section">
              <h5 className="debug-console-subsection-title">🔒 Permissions & Access</h5>
              {['roles', 'role', 'entities', 'zones', 'currencies', 'departments', 'levels'].map(key => {
                // Définir des valeurs par défaut si l'attribut n'existe pas
                let value = currentAttributes[key]
                if (value === undefined || value === null) {
                  if (key === 'roles' || key === 'entities' || key === 'zones' || key === 'currencies' || key === 'departments') {
                    value = []
                  } else if (key === 'levels') {
                    value = { n1: false, n2: false, n3: false }
                  } else if (key === 'role') {
                    value = ''
                  }
                }
                
                return (
                  <div key={key} className="debug-console-attribute-group">
                    <label className="debug-console-attribute-label">
                      {key}
                      {renderSpecializedAttributeEditor(key, value)}
                    </label>
                  </div>
                )
              })}
            </div>

            {/* User Info Section */}
            <div className="debug-console-section">
              <h5 className="debug-console-subsection-title">👤 User Information</h5>
              {['name', 'email', 'firstname', 'lastname'].map(key => {
                if (!(key in currentAttributes)) return null
                const value = currentAttributes[key]
                
                return (
                  <div key={key} className="debug-console-attribute-group">
                    <label className="debug-console-attribute-label">
                      {key}
                      {renderSpecializedAttributeEditor(key, value)}
                    </label>
                  </div>
                )
              })}
            </div>

            {/* Account Settings Section */}
            <div className="debug-console-section">
              <h5 className="debug-console-subsection-title">⚙️ Account Settings</h5>
              {['is_email_verified', 'is_disabled', 'has_completed_signup', 'superadmin'].map(key => {
                if (!(key in currentAttributes)) return null
                const value = currentAttributes[key]
                
                return (
                  <div key={key} className="debug-console-attribute-group">
                    <label className="debug-console-attribute-label">
                      {key}
                      {renderSpecializedAttributeEditor(key, value)}
                    </label>
                  </div>
                )
              })}
            </div>

            {/* Other Attributes Section */}
            <div className="debug-console-section">
              <h5 className="debug-console-subsection-title">📋 Other Attributes</h5>
              {Object.entries(currentAttributes).map(([key, value]) => {
                // Exclure les champs techniques et ceux déjà affichés
                const excludeFields = [
                  'id', 'iat', 'exp', 'impersonation', 
                  'roles', 'role', 'entities', 'zones', 'currencies', 'departments', 'levels',
                  'name', 'email', 'firstname', 'lastname',
                  'is_email_verified', 'is_disabled', 'has_completed_signup', 'superadmin',
                ]
                if (excludeFields.includes(key)) return null

                return (
                  <div key={key} className="debug-console-attribute-group">
                    <label className="debug-console-attribute-label">
                      {key}
                      {renderSpecializedAttributeEditor(key, value)}
                    </label>
                  </div>
                )
              })}
            </div>

            <div className="debug-console-attribute-actions">
              <div className="debug-console-button-group">
                <button
                  type="button"
                  className="debug-console-step-button"
                  onClick={handleSaveAttributes}
                  disabled={isSavingAttributes || attributesSaved}
                  title={attributesSaved ? "Attributes already saved" : "Save custom attributes to session storage"}
                >
                  {isSavingAttributes ? '⏳ Saving...' : attributesSaved ? '✅ 1. Saved' : '💾 1. Save Changes'}
                </button>
                <button
                  type="button"
                  className="debug-console-step-button"
                  onClick={handleRefreshData}
                  disabled={isRefreshing || isLoading || !attributesSaved}
                  title={!attributesSaved ? "Save attributes first" : "Refresh database with new filter attributes"}
                >
                  {isRefreshing ? '⏳ Refreshing...' : '🔄 2. Refresh Database'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="debug-console-attributes-viewer">
            <div className="debug-console-viewer-hint">
              All user attributes are shown in the 'User' tab above. Click Edit to modify.
            </div>
          </div>
        )}
      </div>
    )
  }

  if (!isDevelopment && !window.location.search.includes('debug=true')) {
    return null
  }

  if (isHidden) {
    return (
      <div className="debug-console-badge">
        <button
          type="button"
          onClick={() => setIsHidden(false)}
          className="debug-console-badge-button"
          title="Show Debug Console"
        >
          🔧
        </button>
      </div>
    )
  }

  return (
    <div className="debug-console">
      <div className="debug-console-header">
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className="debug-console-title"
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsExpanded(!isExpanded)
            }
          }}
        >
          <span>🔧</span>
          <span>Debug</span>
          {isDevelopment && (
            <span className="debug-console-dev-badge">
              DEV
            </span>
          )}
        </div>

        <div className="debug-console-controls">
          <div className="debug-console-user-info">
            {isDevelopment && availableUsers.length > 0 && (
              <button
                type="button"
                onClick={handlePreviousUser}
                className="debug-console-nav-arrow"
                title="Previous user"
              >
                ⬅️
              </button>
            )}
            <span className="debug-console-user-text">
              {user?.name || 'No user'}
              {' '}
              (
              {user?.role || 'N/A'}
              )
              {isDevelopment && availableUsers.length > 0 && (
                <span className="debug-console-user-position">
                  {availableUsers.findIndex(u => u.email === user?.email) + 1}
                  /
                  {availableUsers.length}
                </span>
              )}
            </span>
            {isDevelopment && availableUsers.length > 0 && (
              <button
                type="button"
                onClick={handleNextUser}
                className="debug-console-nav-arrow"
                title="Next user"
              >
                ➡️
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={`debug-console-button ${isExpanded ? 'expanded' : ''}`}
            title={isExpanded ? "Collapse Debug Console" : "Expand Debug Console"}
            aria-expanded={isExpanded}
            aria-controls="debug-console-content"
          >
            ⚙️
          </button>

          {(isBackendMode || (isDevelopment && availableUsers.length > 0)) && (
            <div style={{ position: 'relative' }}>
              {isBackendMode && isCurrentlyImpersonating && (
                <button
                  type="button"
                  onClick={handleStopImpersonation}
                  className="debug-console-stop-impersonation"
                  title={`Stop impersonating ${impersonationStatus?.target_user?.email}`}
                >
                  🚪 Stop Impersonation
                </button>
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (isBackendMode && backendUsers.length === 0) {
                    loadBackendUsers()
                  }
                  setUserDropdownOpen(!userDropdownOpen)
                }}
                className="debug-console-switch-user"
                disabled={isBackendMode && isLoadingUsers}
              >
                👤
                {' '}
                {isLoadingUsers ? 'Loading...' : 'Switch User'}
              </button>

              {userDropdownOpen && (
                <div className="debug-console-dropdown">
                  <div className="debug-console-search">
                    <label htmlFor="user-search" className="sr-only">
                      Search users by name, email, role, or entity
                    </label>
                    <input
                      id="user-search"
                      type="text"
                      placeholder="Search users by name, email, role, or entity..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="debug-console-search-input"
                    />
                    <div className="debug-console-search-count">
                      {filteredUsers.length}
                      {' '}
                      user(s) found
                    </div>
                  </div>

                  <div className="debug-console-users-list">
                    {filteredUsers.length > 0 ? filteredUsers.map(availableUser => (
                      <button
                        type="button"
                        key={isBackendMode ? availableUser.id : availableUser.email}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleUserChange(isBackendMode ? availableUser.id : availableUser.email)
                        }}
                        className={`debug-console-user-item ${user?.email === availableUser.email ? 'active' : ''}`}
                      >
                        <div className="debug-console-user-header">
                          <div className="debug-console-user-name">
                            {availableUser.name}
                          </div>
                          <div>
                            {user?.email === availableUser.email && (
                              <span className="debug-console-current-badge">
                                CURRENT
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="debug-console-user-email">
                          {availableUser.email}
                        </div>
                        <div className="debug-console-user-details">
                          <span className="debug-console-user-role">
                            {availableUser.role}
                          </span>
                          <span>{Array.isArray(availableUser.entities) ? availableUser.entities.join(', ') : availableUser.entities}</span>
                          {availableUser.currencies && (
                            <span style={{ marginLeft: '3px', color: '#666' }}>
                              •
                              {' '}
                              {Array.isArray(availableUser.currencies)
                                ? availableUser.currencies.join(', ')
                                : availableUser.currencies}
                            </span>
                          )}
                        </div>
                      </button>
                    )) : (
                      <div className="debug-console-no-results">
                        No users found matching
                        {' '}
                        &quot;
                        {userSearch}
                        &quot;
                        <br />
                        <small className="debug-console-no-results-hint">
                          Try searching by name, email, role, or entity
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="debug-console-dropdown-footer">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setUserDropdownOpen(false)
                        setUserSearch('')
                      }}
                      className="debug-console-close-button"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsHidden(true)}
            className="debug-console-button"
            title="Hide Debug Console"
          >
            💨
          </button>
        </div>
      </div>

      {isExpanded && (
        <div id="debug-console-content" className="debug-console-content">
          <div className="debug-console-tabs">
            {['user', 'data', 'permissions', 'cache'].map(tab => (
              <button
                type="button"
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`debug-console-tab ${activeTab === tab ? 'active' : ''}`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="debug-console-tab-content">
            {activeTab === 'user' && (
              <div>
                {isCurrentlyImpersonating && (
                  <div className="debug-console-impersonation-banner">
                    🎭 Impersonating:
                    {' '}
                    <strong>{impersonationStatus.target_user.email}</strong>
                    <br />
                    <small>
                      Admin:
                      {impersonationStatus.admin_user.email}
                    </small>
                  </div>
                )}

                <div className="debug-console-user-data">
                  <h4 className="debug-console-section-title">
                    User Information
                    <button
                      type="button"
                      onClick={async () => {
                        const completeUser = await loadCompleteUserAttributes()
                        if (completeUser) {
                          console.log('🔍 [DebugConsole] Complete user loaded:', completeUser)
                          showToast({ message: '✅ Attributs complets chargés !', type: 'success', duration: 3000 })
                        }
                      }}
                      className="debug-console-load-attributes-button"
                      title="Load complete user attributes from database"
                    >
                      🔍 Load Complete Attributes
                    </button>
                  </h4>
                  <div className="debug-console-user-properties">
                    {Object.entries(userInfo.data).map(([key, value]) => (
                      <div key={key} className="debug-console-property-row">
                        <span className="debug-console-property-label">
                          {key}
                          :
                        </span>
                        <span className="debug-console-property-value">{value}</span>
                      </div>
                    ))}
                  </div>

                  {Object.keys(userInfo.data).length === 0 && (
                    <div className="debug-console-no-data">
                      {userInfo.status}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'data' && (
              <div className="debug-console-data-section">
                <div className="debug-console-data-header">
                  <div className="debug-console-grid">
                    <span className="debug-console-label">Status:</span>
                    <span className={`debug-console-status ${isLoading ? 'loading' : ''}`}>
                      {isLoading ? 'Loading...' : 'Ready'}
                    </span>
                    <span className="debug-console-label">Sales Orders:</span>
                    <span>
                      {dashboardSalesOrders.length}
                      {' '}
                      records
                    </span>
                    <span className="debug-console-label">Bank Transfers:</span>
                    <span>
                      {bankTransfers.length}
                      {' '}
                      records
                    </span>
                    <span className="debug-console-label">Matches:</span>
                    <span>
                      {transfersOrdersMatches.length}
                      {' '}
                      records
                    </span>
                  </div>
                </div>

                <div className="debug-console-data-actions">
                  <button
                    type="button"
                    onClick={handleRefreshData}
                    disabled={isRefreshing || isLoading}
                    className="debug-console-refresh-button"
                    title="Refresh all database collections"
                  >
                    <span style={{
                      display: 'inline-block',
                      animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                      marginRight: '6px',
                    }}
                    >
                      🔄
                    </span>
                    {isRefreshing ? 'Refreshing...' : 'Refresh Database'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'permissions' && renderSuperAdminControls()}

            {activeTab === 'cache' && (
              <div className="debug-console-cache-section">
                <h4 className="debug-console-section-title">Cache Stats</h4>
                <div className="debug-console-cache-stats">
                  <div className="debug-console-cache-stat-group">
                    <span className="debug-console-cache-stat-label">Total Cache Size:</span>
                    <span className="debug-console-cache-stat-value">
                      {formatBytes(cacheStats.totalSize)}
                      {cacheStats.wasTruncated && ' (estimated)'}
                    </span>
                  </div>
                  <div className="debug-console-cache-stat-group">
                    <span className="debug-console-cache-stat-label">Total Records in Cache:</span>
                    <span className="debug-console-cache-stat-value">{cacheStats.totalRecords}</span>
                  </div>
                  <div className="debug-console-cache-stat-group">
                    <span className="debug-console-cache-stat-label">Records Analyzed:</span>
                    <span className="debug-console-cache-stat-value">
                      {cacheStats.analyzedRecords || 0}
                      {cacheStats.wasTruncated && ' (limited for performance)'}
                    </span>
                  </div>
                  {cacheStats.wasTruncated && (
                    <div className="debug-console-cache-warning">
                      ⚠️ Large cache detected - analysis limited to prevent memory issues
                    </div>
                  )}
                  {cacheStats.error && (
                    <div className="debug-console-cache-error">
                      ❌ Error:
                      {' '}
                      {cacheStats.error}
                    </div>
                  )}
                  {Object.entries(cacheStats.userStats || {}).length > 0 && (
                    <>
                      <h5 className="debug-console-section-title">User-Specific Cache Stats</h5>
                      {Object.entries(cacheStats.userStats).map(
                        ([userEmail, userStats]) => (
                          <div key={userEmail} className="debug-console-user-cache-stats">
                            <h6>
                              User:
                              {' '}
                              {userEmail}
                              {userStats.isEstimated && ' (estimated)'}
                            </h6>
                            <div className="debug-console-cache-stat-group">
                              <span className="debug-console-cache-stat-label">Cache Size:</span>
                              <span className="debug-console-cache-stat-value">{formatBytes(userStats.size)}</span>
                            </div>
                            <div className="debug-console-cache-stat-group">
                              <span className="debug-console-cache-stat-label">Collections:</span>
                              <span className="debug-console-cache-stat-value">{userStats.collections}</span>
                            </div>
                          </div>
                        ),
                      )}
                    </>
                  )}
                </div>
                <div className="debug-console-cache-actions">
                  <button
                    type="button"
                    onClick={handleClearCache}
                    disabled={isClearingCache}
                    className="debug-console-clear-cache-button"
                  >
                    {isClearingCache ? 'Clearing...' : 'Clear All Cache'}
                  </button>
                  <button
                    type="button"
                    onClick={loadCacheStats}
                    disabled={isClearingCache}
                    className="debug-console-refresh-stats-button"
                    title="Refresh cache statistics"
                  >
                    🔄 Refresh Stats
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {userDropdownOpen && (
        <div
          className="debug-console-overlay"
          onClick={() => {
            setUserDropdownOpen(false)
            setUserSearch('')
          }}
          role="button"
          tabIndex={-1}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setUserDropdownOpen(false)
              setUserSearch('')
            }
          }}
        />
      )}
    </div>
  )
}

export default DebugConsole
